import { useReducer } from 'react';
import { PermissionType } from '../../constants/types';
import { PermissionRules } from '../../constants/permissions';
import {
  FeatureFlagName,
  UserInterface,
  UserLoginCredentialsInterface
} from '../../interfaces/User.interface';
import ApiClient from '../../api-client/ApiClient';

interface AuthStoreData {
  user: UserInterface | null;
  isAuthed: boolean;
  token: string | null;
  qrCode: string | null; // NEW – stores MFA QR code if applicable
  secretKey: string | null; // NEW – stores MFA secret key
  mfaOption: 'app' | 'sms' | null; // NEW – selected MFA option
  userLoginCredentials: UserLoginCredentialsInterface | null; // NEW – cached login creds
}

export interface AuthStoreType {
  user: UserInterface | null;
  isAuthed: boolean;
  token: string | null;
  qrCode: string | null; // NEW
  secretKey: string | null; // NEW
  mfaOption: 'app' | 'sms' | null; // NEW
  userLoginCredentials: UserLoginCredentialsInterface | null; // NEW
  setUser: (user: UserInterface | null) => void;
  setToken: (token: string | null) => void;
  refreshUser: () => Promise<void>;
  hasPermission: (permissions: PermissionType[]) => boolean;
  hasEveryPermission: (permissions: PermissionType[]) => boolean;
  hasFeature: (flag: FeatureFlagName) => boolean;
  // NEW setters for reconciled functionality
  setQrCode: (code: string | null) => void;
  setSecretKey: (key: string | null) => void;
  setMfaOption: (option: 'app' | 'sms' | null) => void;
  setUserLoginCredentials: (
    credentials: UserLoginCredentialsInterface | null
  ) => void;
}

export default function useAuthStore(): AuthStoreType {
  const [store, setStore] = useReducer(
    (prev: AuthStoreData, newState: Partial<AuthStoreData>) => ({
      ...prev,
      ...newState
    }),
    {
      user: null,
      isAuthed: false,
      token: localStorage.getItem('token'),
      qrCode: null,
      secretKey: null,
      mfaOption: null,
      userLoginCredentials: null
    }
  );

  const apiClient = new ApiClient();

  const setUser = (user: UserInterface | null) => {
    if (!user) {
      setStore({ user: null, isAuthed: false });
      return;
    }

    const data = { ...user } as UserInterface;
    if (data?.roles) {
      // Extract permission keys from user object
      const RuleValues = Object.values(PermissionRules);
      const permissionKeys: string[] = [];
      data.roles.forEach((role: { permissions?: Array<{ name: string }> }) => {
        if (!role.permissions) return;
        role.permissions.forEach((permission: { name: string }) => {
          const index = RuleValues.indexOf(permission.name);
          if (index !== -1) {
            permissionKeys.push(Object.keys(PermissionRules)[index]);
          }
        });
      });
      data.permissions = permissionKeys;
    }

    setStore({ user: data, isAuthed: true });
  };

  const setToken = (token: string | null) => {
    const key = 'token';
    if (token) {
      localStorage.setItem(key, token);
    } else {
      localStorage.removeItem(key);
    }
    setStore({ token });
  };

  const refreshUser = async () => {
    try {
      const [user, myCompany, featureFlags] = await Promise.all([
        apiClient.get('/me').then((res) => res.data),
        apiClient.get('/company/tenant-parent').then((res) => res.data),
        apiClient.get('/me/feature').then((res) => res.data.data)
      ]);

      if (user) {
        const userData = { ...user } as UserInterface;
        userData.company = myCompany;

        userData.hasCedarPermission =
          featureFlags?.[0]?.features?.[
            'Can view Cedar broker portal login link'
          ] ?? false;

        userData.canMakePhoneCalls =
          featureFlags?.[0]?.features?.['Can make phone calls'] ?? false;

        userData.canManageSavingsAccounts =
          featureFlags?.[0]?.features?.['Can manage savings accounts'] ?? false;

        userData.canAccessMeetings =
          featureFlags?.[0]?.features?.['Can access meetings'] ??
          featureFlags?.[0]?.features?.['Relationship Intelligence'] ??
          false;

        userData.feature_flags = {
          pipeline_stage_workflows:
            user.feature_flags?.pipeline_stage_workflows ?? false,
          audience_presets: user.feature_flags?.audience_presets ?? false,
          relationship_intelligence:
            user.feature_flags?.relationship_intelligence ?? false,
          meetings: user.feature_flags?.meetings ?? false,
          inbound_calling: user.feature_flags?.inbound_calling ?? false
        };

        setUser(userData);
      } else {
        setUser(null);
        setToken(null);
      }
    } catch (e) {
      console.error('[Auth Store] Failed to refresh user:', e);
      setUser(null);
      setToken(null);
    }
  };

  const hasPermission = (permissions: PermissionType[] | undefined): boolean => {
    if (!permissions?.length) return false;

    let state = false;
    const userPermissions = store.user?.permissions;

    if (!userPermissions || userPermissions.length === 0) return false;

    permissions.forEach((permission) => {
      if (userPermissions.indexOf(permission) !== -1) {
        state = true;
      }
    });

    return state;
  };

  const hasEveryPermission = (permissions: PermissionType[] | undefined): boolean => {
    if (!permissions?.length) return false;

    const userPermissions = store.user?.permissions;
    if (!userPermissions || userPermissions.length === 0) return false;

    return permissions.every((permission) => userPermissions.indexOf(permission) !== -1);
  };

  const hasFeature = (flag: FeatureFlagName): boolean =>
    !!store.user?.feature_flags?.[flag];

  const setQrCode = (code: string | null) => {
    setStore({ qrCode: code });
  };

  const setSecretKey = (key: string | null) => {
    setStore({ secretKey: key });
  };

  const setMfaOption = (option: 'app' | 'sms' | null) => {
    setStore({ mfaOption: option });
  };

  const setUserLoginCredentials = (
    credentials: UserLoginCredentialsInterface | null
  ) => {
    setStore({ userLoginCredentials: credentials });
  };

  return {
    user: store.user,
    isAuthed: store.isAuthed,
    token: store.token,
    qrCode: store.qrCode,
    secretKey: store.secretKey,
    mfaOption: store.mfaOption,
    userLoginCredentials: store.userLoginCredentials,
    setUser,
    setToken,
    refreshUser,
    hasPermission,
    hasEveryPermission,
    hasFeature,
    setQrCode,
    setSecretKey,
    setMfaOption,
    setUserLoginCredentials
  };
}
